/**
 * Jovii — cast photos, browser-direct and commercially unencumbered.
 *
 * Cinemeta carries three cast NAMES and no photos (docs/CAST.md § A), so the faces
 * come from elsewhere, and the owner's constraint is that "elsewhere" must stay
 * free even for commercial use. The order of preference for the DATA (names,
 * characters, ids), every number measured in docs/CAST.md:
 *
 *  1. TMDB — ONLY when the owner has pasted a key into Settings
 *     (`jt.tmdbKey.v1`, the SubDL pattern). PERSONAL-USE ENRICHMENT: TMDB's free
 *     tier is non-commercial, so a commercial deployment simply never sets the
 *     key and ships the sources below.
 *  2. Wikidata's work chain — find the film/show by the IMDb id we already hold
 *     (P345), read its own cast statements (P161, plus P725 for voice cast) with
 *     P453 character qualifiers, portrait from P18 on Wikimedia Commons. The data
 *     is CC0; the images are individually-licensed free files whose attribution
 *     is retrievable. Ids end to end, so the wrong-person failure of name lookup
 *     cannot happen — and the person's own Wikidata id (`qid`) rides along on
 *     each member so a face can link to `/person/Q…`. 10/10 titles matched, and
 *     movies get their WHOLE cast, not Cinemeta's three.
 *  3. TVMaze, series only — CC BY-SA "for any purpose" with a credit link-back
 *     (terms captured in docs/CAST.md § B/E). 99.6% of people have a portrait,
 *     at 13 KB each.
 *  4. Wikidata by NAME, for titles with no work-level cast — top-5 candidates
 *     filtered by occupation (P106 ∈ actor kinds). Measured: never returned a
 *     wrong person, recovered the one the naive first-hit lost, and prefers "no
 *     photo" over a guess (docs/CAST.md § D).
 *
 * THE PHOTO, though, now prefers TVMaze's curated portraits over Commons P18
 * even when Wikidata answered the data. Measured 2026-08-17 over 32 faces from
 * 8 popular titles (docs/CAST.md § K): Commons P18 picks were professional
 * headshots in 2 of 12 cases — the free-licence pool is red-carpet and
 * convention candids, because studio portraits are copyrighted — while TVMaze's
 * editor-curated portraits were headshot-grade in 18 of 20. So: a series row
 * takes portraits from the show's own TVMaze cast (2 extra calls, matched by
 * folded name); a movie row asks TVMaze's people search per member (≤16 small
 * calls, two at a time), accepting a hit only when the birthday matches the
 * person's Wikidata P569 or the exact name is unique — measured: five TVMaze
 * people share the exact name "Rebecca Ferguson", and the birthday test picks
 * the actress, not the singer. Commons (with P18's preferred-rank statement
 * honoured over claim order) remains the fallback, and the fully
 * commercial-safe posture of the old chain is recovered by simply not calling
 * the two `tvmaze*Portraits` helpers.
 *
 * No photo from anywhere = the initials plate, which `Art.svelte` keeps
 * underneath the image at all times — the floor, not a branch.
 *
 * Every host here sends `ACAO: *` on its JSON (Wikidata via `origin=*`, TVMaze,
 * TMDB even on its 401); images load as plain `<img>`, which needs no CORS and is
 * one more reason COOP/COEP stay off. upload.wikimedia.org rate-limits aggressive
 * bursts (measured 429s under the probe's profile) — a title page's JSON calls
 * and ≤16 lazy images are nothing like that, and a 429'd face degrades to the
 * plate.
 *
 * Owned by the CAST lane. Imported by CastRow, Title, Settings and person.ts
 * (the `/person/:key` page reuses the Wikidata wire shapes and helpers here).
 */

import type { MediaKind } from '$types';
import { sharedRequests } from './shared-request';
import { catalogueDeadline, CAST_DEADLINE_MS } from './catalogue-deadline';
import { KEYS, readJson, writeJson } from './storage';

/* ────────────────────────────────────────────────────────────────────────────
 * Shapes
 * ──────────────────────────────────────────────────────────────────────────── */

export interface CastMember {
  name: string;
  /** Character name, when the source states one. */
  character?: string;
  /** Face URL. Absent = the initials plate, deliberately. */
  photo?: string;
  /** Responsive variants for the bubble ("url 120w, url 250w"). */
  srcset?: string;
  /**
   * The photo's own source page — a Commons file page (the author + licence
   * record), a TVMaze person page (their terms' link-back), or a TMDB person
   * page. This is the attribution surface: the face's `title` text carries it,
   * and the person page shows it as a visible credit link.
   */
  creditUrl?: string;
  /**
   * The person's own Wikidata id, when the work chain resolved one. This is the
   * key `/person/:key` prefers; a member without one links by name and the
   * person page re-resolves (see `personHref` in nav.svelte.ts).
   */
  qid?: string;
  /**
   * Date of birth as `YYYY-MM-DD`, when Wikidata states it to day precision.
   * Not rendered on the row — it is the disambiguator for TVMaze's people
   * search, where five people can share one exact name (measured, § header).
   */
  born?: string;
}

/** 'tmdb' rows are the ceiling; 'keyless' rows refetch once a key appears. */
type Tier = 'keyless' | 'tmdb';

/**
 * Bumped to 2 when members gained `qid` and the TVMaze portrait preference —
 * a cached v1 row would keep the old candid for a week and could not link its
 * faces by id. Entries without the current version read as misses and are
 * re-fetched; the prune retires them from disk. (storage.ts's own rule is
 * "schema change = new key", honoured here as a version field so this lane
 * does not have to edit storage.ts.)
 */
const CACHE_VERSION = 2;

interface CachedTitle {
  at: number;
  tier: Tier;
  members: CastMember[];
  v?: number;
}

export interface CastQuery {
  kind: MediaKind;
  imdbId: string;
  /** Cinemeta's billed names — the ordering hint, and the name-fallback input. */
  names?: string[];
  /** Cinemeta's `moviedb_id`, which saves TMDB's /find round trip. */
  moviedbId?: number;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Constants
 * ──────────────────────────────────────────────────────────────────────────── */

/** 16 faces ≈ 96 KB at 1× / ~320 KB at 3× via srcset, lazy-loaded (docs/CAST.md § H). */
const MAX_FACES = 16;

const WD_API = 'https://www.wikidata.org/w/api.php';
const COMMONS = 'https://commons.wikimedia.org/wiki';
const TVMAZE = 'https://api.tvmaze.com';
const TMDB_API = 'https://api.themoviedb.org/3';
const TMDB_IMG = 'https://image.tmdb.org/t/p/w185';

/**
 * P106 values that mean "this person acts". Measured against 8 ambiguous names:
 * the filter never accepted a wrong person and recovered the Dune cast member the
 * naive first hit lost (docs/CAST.md § D).
 */
const ACTOR_OCCUPATIONS = new Set([
  'Q33999', // actor
  'Q10800557', // film actor
  'Q10798782', // television actor
  'Q2405480', // voice actor
  'Q2259451' // stage actor
]);

/** A week: cast lists move on the timescale of seasons, not sessions. */
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** ~2 KB a title; 30 titles ≈ 60 KB of localStorage. */
const MAX_CACHED_TITLES = 30;

/* ────────────────────────────────────────────────────────────────────────────
 * The TMDB key — the SubDL pattern: client-side, per-browser, absent = skipped.
 * ──────────────────────────────────────────────────────────────────────────── */

export function tmdbKey(): string | null {
  const value = readJson(KEYS.tmdbKey, '', (raw) => typeof raw === 'string' ? raw.trim() : '');
  return value ? value : null;
}

/** v4 "Read Access Tokens" are JWTs and ride in a header; v3 keys in the query. */
export function isV4Token(key: string): boolean {
  return key.startsWith('eyJ');
}

/* ────────────────────────────────────────────────────────────────────────────
 * Pure pieces, exported for the unit suite
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Commons thumbnail. Wikimedia rejects arbitrary widths now (400, "use listed
 * sizes") — 120/250/330/500 are measured-allowed buckets at 5.6/20/33/75 KB
 * (docs/CAST.md § C3). The row uses 120/250; the person page's one large
 * portrait uses 500 (re-probed 2026-08-17: 200, image/jpeg).
 */
export function commonsThumb(file: string, width: 120 | 250 | 330 | 500): string {
  return `${COMMONS}/Special:FilePath/${encodeURIComponent(file)}?width=${width}`;
}

/** The file's description page — the author + licence record, for the bubble link. */
export function commonsFilePage(file: string): string {
  return `${COMMONS}/File:${encodeURIComponent(file.replace(/ /g, '_'))}`;
}

/** Accent- and case-insensitive name key: "Timothée" and "timothee" collide. */
export function foldName(s: string): string {
  return s
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();
}

/**
 * Wikidata claim order is not billing order (Breaking Bad's first cast claim is
 * Anna Gunn). Cinemeta's three names ARE top billing, so members matching them
 * move to the front in billed order; everyone else keeps the source order.
 * Matching is accent- and case-insensitive: Cinemeta says "Timothee" where
 * Wikidata says "Timothée".
 */
export function reorderBilled(members: CastMember[], billed: string[]): CastMember[] {
  if (!billed.length || members.length < 2) return members;
  const byName = new Map<string, CastMember>();
  for (const m of members) if (!byName.has(foldName(m.name))) byName.set(foldName(m.name), m);
  const front: CastMember[] = [];
  const taken = new Set<CastMember>();
  for (const name of billed) {
    const hit = byName.get(foldName(name));
    if (hit && !taken.has(hit)) {
      front.push(hit);
      taken.add(hit);
    }
  }
  if (!front.length) return members;
  return [...front, ...members.filter((m) => !taken.has(m))];
}

/**
 * Loosely-typed Wikidata wire shapes — everything optional, nothing trusted.
 * Exported (with the two claim readers) for person.ts, so the `/person` page
 * parses entities with exactly the same guards as the row.
 */
export interface WdSnak {
  mainsnak?: { datavalue?: { value?: { id?: string; time?: string; precision?: number } | string } };
  qualifiers?: Record<string, { datavalue?: { value?: { id?: string } } }[]>;
  rank?: string;
}
export interface WdEntity {
  labels?: Record<string, { value?: string }>;
  descriptions?: Record<string, { value?: string }>;
  sitelinks?: Record<string, { title?: string }>;
  claims?: Record<string, WdSnak[]>;
}

export function claimItemId(claim: WdSnak | undefined): string | null {
  const value = claim?.mainsnak?.datavalue?.value;
  return typeof value === 'object' && typeof value?.id === 'string' ? value.id : null;
}

export function claimString(claim: WdSnak | undefined): string | null {
  const value = claim?.mainsnak?.datavalue?.value;
  return typeof value === 'string' && value ? value : null;
}

/**
 * A time claim as `YYYY-MM-DD`, only when Wikidata states it to day precision
 * (11). The wire shape is `+1983-10-19T00:00:00Z`; year-precision entries carry
 * zero months/days that would never match a real birthday, so they are refused
 * rather than half-matched. (Probed live 2026-08-17: P569/P570 both this shape.)
 */
export function claimDate(claim: WdSnak | undefined): string | null {
  const value = claim?.mainsnak?.datavalue?.value;
  if (typeof value !== 'object' || typeof value?.time !== 'string') return null;
  if (typeof value.precision === 'number' && value.precision < 11) return null;
  const m = /^\+(\d{4}-\d{2}-\d{2})T/.exec(value.time);
  if (!m || m[1]!.includes('-00')) return null;
  return m[1]!;
}

/**
 * The image statement to use: a `preferred`-rank P18 over claim order. Probed
 * live 2026-08-17: Keanu Reeves' first P18 claim is a normal-rank 2025 TIFF
 * candid while a preferred-rank statement exists; Bryan Cranston's preferred
 * P18 is a studio portrait, his first claim a 3×4 crop. Taking `[0]` was one
 * measured reason candids won (docs/CAST.md § K).
 */
export function bestImageFile(claims: WdSnak[] | undefined): string | null {
  if (!claims?.length) return null;
  const preferred = claims.find((c) => c.rank === 'preferred');
  return claimString(preferred) ?? claimString(claims.find((c) => claimString(c) !== null));
}

export interface WorkPick {
  actor: string;
  character: string | null;
}

/**
 * The cast statements of a work: P161 (cast member) then P725 (voice actor —
 * anime lives there and nowhere else: Attack on Titan has 0 × P161, 37 × P725).
 * De-duplicated on the actor id, capped, character from the P453 qualifier.
 */
export function pickWorkCast(claims: Record<string, WdSnak[]> | undefined, max = MAX_FACES): WorkPick[] {
  const stated = [...(claims?.['P161'] ?? []), ...(claims?.['P725'] ?? [])];
  const picks: WorkPick[] = [];
  const seen = new Set<string>();
  for (const claim of stated) {
    const actor = claimItemId(claim);
    if (!actor || seen.has(actor)) continue;
    seen.add(actor);
    const character = claim.qualifiers?.['P453']?.[0]?.datavalue?.value?.id ?? null;
    picks.push({ actor, character });
    if (picks.length >= max) break;
  }
  return picks;
}

/**
 * Attach a Commons portrait to a member. JPEGs offer both buckets and let DPR
 * choose; PNGs are pinned to 120 px, because a PNG face at 250 px is 60–90 KB
 * where a JPEG is ~20 KB — the Squid Game row measured 772 KB before this pin
 * and a slightly soft PNG beats a blown byte budget.
 */
export function attachCommonsPhoto(member: CastMember, file: string): void {
  if (/\.png$/i.test(file)) {
    member.photo = commonsThumb(file, 120);
  } else {
    member.photo = commonsThumb(file, 250);
    member.srcset = `${commonsThumb(file, 120)} 120w, ${commonsThumb(file, 250)} 250w`;
  }
  member.creditUrl = commonsFilePage(file);
}

/** Picks + their fetched entities → members. Missing labels drop the member. */
export function membersFromEntities(picks: WorkPick[], entities: Record<string, WdEntity | undefined>): CastMember[] {
  const members: CastMember[] = [];
  for (const pick of picks) {
    const entity = entities[pick.actor];
    const name = entity?.labels?.['en']?.value;
    if (!name) continue;
    const member: CastMember = { name, qid: pick.actor };
    const character = pick.character ? entities[pick.character]?.labels?.['en']?.value : undefined;
    if (character) member.character = character;
    const born = claimDate(entity?.claims?.['P569']?.[0]);
    if (born) member.born = born;
    const file = bestImageFile(entity?.claims?.['P18']);
    if (file) attachCommonsPhoto(member, file);
    members.push(member);
  }
  return members;
}

/* ────────────────────────────────────────────────────────────────────────────
 * TVMaze portraits — the quality preference (header, and docs/CAST.md § K)
 * ──────────────────────────────────────────────────────────────────────────── */

/** A curated TVMaze portrait and the link-back page its licence asks for. */
export interface Portrait {
  photo: string;
  /** The full-size original, for the person page. */
  large?: string;
  creditUrl: string;
}

interface TvmazePerson {
  id?: number;
  name?: string;
  url?: string;
  birthday?: string | null;
  image?: { medium?: string; original?: string } | null;
}

function portraitOf(person: TvmazePerson): Portrait | null {
  if (!person.image?.medium || !person.url) return null;
  const portrait: Portrait = { photo: person.image.medium, creditUrl: person.url };
  if (person.image.original) portrait.large = person.image.original;
  return portrait;
}

/** TVMaze `/shows/{id}/cast` → folded name → portrait. No ambiguity: the person
 *  is cast in THIS show, so a name collision would be within one cast list. */
export function mapTvmazeShowPortraits(raw: unknown): Map<string, Portrait> {
  const portraits = new Map<string, Portrait>();
  if (!Array.isArray(raw)) return portraits;
  for (const entry of raw as { person?: TvmazePerson }[]) {
    const person = entry?.person;
    if (!person?.name) continue;
    const key = foldName(person.name);
    if (portraits.has(key)) continue;
    const portrait = portraitOf(person);
    if (portrait) portraits.set(key, portrait);
  }
  return portraits;
}

/**
 * One `/search/people` answer → a portrait, or null. Name search is where wrong
 * people come from, so the gate is strict (measured 2026-08-17: FIVE TVMaze
 * people are exactly "Rebecca Ferguson" — the actress and the singer both):
 *
 *  - only exact folded-name matches are candidates;
 *  - with a known Wikidata birthday (P569) AND any candidate carrying one,
 *    the birthday must match — that check picked the actress (1983-10-19)
 *    over the singer (1986-07-21);
 *  - when no candidate has a birthday to compare (measured: TVMaze's one
 *    exact "Ken Watanabe" has `birthday: null`), or we have none ourselves,
 *    the exact name must be UNIQUE. Two exact strangers = no photo; the
 *    Commons pick or the plate beats a coin toss.
 */
export function mapTvmazePersonSearch(raw: unknown, name: string, born?: string): Portrait | null {
  if (!Array.isArray(raw)) return null;
  const wanted = foldName(name);
  const exact = (raw as { person?: TvmazePerson }[])
    .map((entry) => entry?.person)
    .filter((p): p is TvmazePerson => !!p?.name && foldName(p.name) === wanted);
  if (!exact.length) return null;
  if (born && exact.some((p) => !!p.birthday)) {
    const match = exact.find((p) => p.birthday === born);
    return match ? portraitOf(match) : null;
  }
  return exact.length === 1 ? portraitOf(exact[0]!) : null;
}

/**
 * The merge: a member with a curated portrait takes it — photo, credit link and
 * all — and drops the Commons `srcset` (TVMaze portraits are one 210×295 file,
 * ~13 KB, under the Commons 250px bucket's ~20 KB, so the row's byte budget in
 * docs/CAST.md § H only improves). Members without one keep what they had.
 */
export function preferPortraits(members: CastMember[], portraits: Map<string, Portrait>): CastMember[] {
  if (!portraits.size) return members;
  return members.map((member) => {
    const portrait = portraits.get(foldName(member.name));
    if (!portrait) return member;
    const upgraded: CastMember = { ...member, photo: portrait.photo, creditUrl: portrait.creditUrl };
    delete upgraded.srcset;
    return upgraded;
  });
}

/**
 * The disambiguation lever for name lookup: the first candidate whose P106 says
 * they act. Returns null rather than guessing — the plate beats a wrong face.
 */
export function pickActor(candidates: string[], entities: Record<string, WdEntity | undefined>): string | null {
  for (const id of candidates) {
    const occupations = entities[id]?.claims?.['P106'] ?? [];
    if (occupations.some((c) => ACTOR_OCCUPATIONS.has(claimItemId(c) ?? ''))) return id;
  }
  return null;
}

/** TVMaze `/shows/{id}/cast` → members. One person can appear once per character. */
export function mapTvmazeCast(raw: unknown, max = MAX_FACES): CastMember[] {
  if (!Array.isArray(raw)) return [];
  const members: CastMember[] = [];
  const seen = new Set<number>();
  for (const entry of raw as {
    person?: { id?: number; name?: string; url?: string; image?: { medium?: string } };
    character?: { name?: string };
  }[]) {
    const person = entry?.person;
    if (!person?.name || typeof person.id !== 'number' || seen.has(person.id)) continue;
    seen.add(person.id);
    const member: CastMember = { name: person.name };
    if (entry.character?.name) member.character = entry.character.name;
    if (person.image?.medium) {
      member.photo = person.image.medium;
      // The link-back TVMaze's terms name as satisfying attribution.
      if (person.url) member.creditUrl = person.url;
    }
    members.push(member);
    if (members.length >= max) break;
  }
  return members;
}

interface TmdbCastEntry {
  id?: number;
  name?: string;
  character?: string;
  order?: number;
  profile_path?: string | null;
  roles?: { character?: string; episode_count?: number }[];
}

function tmdbMember(entry: TmdbCastEntry, character: string | undefined): CastMember | null {
  if (!entry?.name) return null;
  const member: CastMember = { name: entry.name };
  if (character) member.character = character;
  if (entry.profile_path) {
    member.photo = `${TMDB_IMG}${entry.profile_path}`;
    if (typeof entry.id === 'number') member.creditUrl = `https://www.themoviedb.org/person/${entry.id}`;
  }
  return member;
}

/** `/movie/{id}/credits` cast, in billing (`order`) order. */
export function mapTmdbMovieCast(cast: unknown, max = MAX_FACES): CastMember[] {
  if (!Array.isArray(cast)) return [];
  return (cast as TmdbCastEntry[])
    .slice()
    .sort((a, b) => (a?.order ?? 999) - (b?.order ?? 999))
    .map((entry) => tmdbMember(entry, entry?.character || undefined))
    .filter((m): m is CastMember => m !== null)
    .slice(0, max);
}

/** `/tv/{id}/aggregate_credits` cast: the character is the most-played role. */
export function mapTmdbAggregateCast(cast: unknown, max = MAX_FACES): CastMember[] {
  if (!Array.isArray(cast)) return [];
  return (cast as TmdbCastEntry[])
    .slice()
    .sort((a, b) => (a?.order ?? 999) - (b?.order ?? 999))
    .map((entry) => {
      const top = (entry?.roles ?? [])
        .slice()
        .sort((a, b) => (b?.episode_count ?? 0) - (a?.episode_count ?? 0))[0];
      return tmdbMember(entry, top?.character || undefined);
    })
    .filter((m): m is CastMember => m !== null)
    .slice(0, max);
}

/** Drop expired entries and keep the newest `max`. Pure, for the tests. */
export function pruneCastCache(
  cache: Record<string, CachedTitle>,
  now: number,
  max = MAX_CACHED_TITLES,
  ttl = CACHE_TTL_MS
): Record<string, CachedTitle> {
  const alive = Object.entries(cache).filter(([, v]) => v && now - v.at < ttl && Array.isArray(v.members));
  alive.sort((a, b) => b[1].at - a[1].at);
  return Object.fromEntries(alive.slice(0, max));
}

/* ────────────────────────────────────────────────────────────────────────────
 * Wire
 * ──────────────────────────────────────────────────────────────────────────── */

async function getJson<T>(url: string | URL, signal: AbortSignal | undefined, headers?: Record<string, string>): Promise<T | null> {
  signal?.throwIfAborted();
  const response = await fetch(url, {
    signal: signal ?? null,
    credentials: 'omit',
    headers: { accept: 'application/json', ...headers }
  });
  signal?.throwIfAborted();
  if (!response.ok) return null;
  const value = (await response.json()) as T;
  signal?.throwIfAborted();
  return value;
}

/** `origin=*` is what makes the MediaWiki API answer with `ACAO: *`. */
function wdUrl(params: Record<string, string>): URL {
  const url = new URL(WD_API);
  url.searchParams.set('format', 'json');
  url.searchParams.set('origin', '*');
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  return url;
}

/** The primary chain: three requests, ids end to end (docs/CAST.md § C1). */
async function wikidataWorkCast(imdbId: string, billed: string[], signal?: AbortSignal): Promise<CastMember[]> {
  const found = await getJson<{ query?: { search?: { title?: string }[] } }>(
    wdUrl({ action: 'query', list: 'search', srsearch: `haswbstatement:P345=${imdbId}`, srlimit: '1' }),
    signal
  );
  const workId = found?.query?.search?.[0]?.title;
  if (!workId || !/^Q\d+$/.test(workId)) return [];

  const work = await getJson<{ entities?: Record<string, WdEntity> }>(
    wdUrl({ action: 'wbgetentities', ids: workId, props: 'claims' }),
    signal
  );
  const picks = pickWorkCast(work?.entities?.[workId]?.claims);
  if (!picks.length) return [];

  const ids = [...new Set(picks.flatMap((p) => (p.character ? [p.actor, p.character] : [p.actor])))];
  const batch = await getJson<{ entities?: Record<string, WdEntity> }>(
    wdUrl({ action: 'wbgetentities', ids: ids.slice(0, 50).join('|'), props: 'labels|claims', languages: 'en' }),
    signal
  );
  return reorderBilled(membersFromEntities(picks, batch?.entities ?? {}), billed);
}

/** Series fallback. The lookup 301s to the show; `ACAO: *` on both hops. */
async function tvmazeCast(imdbId: string, signal?: AbortSignal): Promise<CastMember[]> {
  const show = await getJson<{ id?: number }>(`${TVMAZE}/lookup/shows?imdb=${imdbId}`, signal);
  if (typeof show?.id !== 'number') return [];
  const cast = await getJson<unknown>(`${TVMAZE}/shows/${show.id}/cast`, signal);
  return mapTvmazeCast(cast);
}

/** A series' curated portraits, keyed by folded name. Two small calls. */
async function tvmazeShowPortraits(imdbId: string, signal?: AbortSignal): Promise<Map<string, Portrait>> {
  const show = await getJson<{ id?: number }>(`${TVMAZE}/lookup/shows?imdb=${imdbId}`, signal);
  if (typeof show?.id !== 'number') return new Map();
  const cast = await getJson<unknown>(`${TVMAZE}/shows/${show.id}/cast`, signal);
  return mapTvmazeShowPortraits(cast);
}

/**
 * Movie casts have no show to ask, so each member is one `/search/people` call —
 * two workers, never more than MAX_FACES calls, well inside TVMaze's documented
 * 20-per-10-seconds. The strict match gate lives in `mapTvmazePersonSearch`.
 * Any single failed search just leaves that member's Commons pick standing.
 */
async function tvmazePeoplePortraits(members: CastMember[], signal?: AbortSignal): Promise<Map<string, Portrait>> {
  const portraits = new Map<string, Portrait>();
  const queue = members.slice(0, MAX_FACES);
  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < queue.length) {
      if (signal?.aborted) return;
      const member = queue[cursor++];
      if (!member) return;
      const found = await getJson<unknown>(
        `${TVMAZE}/search/people?q=${encodeURIComponent(member.name)}`,
        signal
      ).catch((err: unknown) => {
        if ((err as Error)?.name === 'AbortError' || (err as Error)?.name === 'TimeoutError') throw err;
        return null;
      });
      const portrait = mapTvmazePersonSearch(found, member.name, member.born);
      if (portrait) portraits.set(foldName(member.name), portrait);
    }
  };
  await Promise.all([worker(), worker()]);
  return portraits;
}

/**
 * The person page's one-portrait variant of the same lookup, exported for
 * person.ts: the large original when TVMaze has it, under the same
 * birthday-or-unique gate. Fails to null, never throws (except on abort).
 */
export async function tvmazePersonPortrait(
  name: string,
  born: string | undefined,
  signal?: AbortSignal
): Promise<Portrait | null> {
  const found = await getJson<unknown>(`${TVMAZE}/search/people?q=${encodeURIComponent(name)}`, signal).catch(
    (err: unknown) => {
      if ((err as Error)?.name === 'AbortError' || (err as Error)?.name === 'TimeoutError') throw err;
      return null;
    }
  );
  return mapTvmazePersonSearch(found, name, born);
}

/** Last resort: Cinemeta's names through search + the P106 occupation filter. */
async function wikidataNameCast(names: string[], signal?: AbortSignal): Promise<CastMember[]> {
  const wanted = names.slice(0, 5);
  const searches = await Promise.all(
    wanted.map(async (name) => {
      const found = await getJson<{ search?: { id?: string }[] }>(
        wdUrl({ action: 'wbsearchentities', search: name, language: 'en', type: 'item', limit: '5' }),
        signal
      ).catch((err: unknown) => {
        if ((err as Error)?.name === 'AbortError' || (err as Error)?.name === 'TimeoutError') throw err;
        return null;
      });
      const ids = (found?.search ?? []).map((s) => s.id).filter((id): id is string => typeof id === 'string');
      return { name, ids };
    })
  );

  const all = [...new Set(searches.flatMap((s) => s.ids))].slice(0, 50);
  let entities: Record<string, WdEntity | undefined> = {};
  if (all.length) {
    const batch = await getJson<{ entities?: Record<string, WdEntity> }>(
      wdUrl({ action: 'wbgetentities', ids: all.join('|'), props: 'labels|claims', languages: 'en' }),
      signal
    );
    entities = batch?.entities ?? {};
  }

  return searches.map(({ name, ids }) => {
    const member: CastMember = { name };
    const hit = pickActor(ids, entities);
    if (hit) {
      member.qid = hit;
      const born = claimDate(entities[hit]?.claims?.['P569']?.[0]);
      if (born) member.born = born;
      const file = bestImageFile(entities[hit]?.claims?.['P18']);
      if (file) attachCommonsPhoto(member, file);
    }
    return member;
  });
}

/**
 * TMDB, when a key exists. PERSONAL-USE ONLY — TMDB's terms make commercial use
 * a negotiated licence, which is why this can never be the default path
 * (docs/CAST.md § G). Any failure returns [] and the keyless chain takes over.
 */
async function tmdbCast(
  kind: MediaKind,
  imdbId: string,
  moviedbId: number | undefined,
  key: string,
  signal?: AbortSignal
): Promise<CastMember[]> {
  const authed = (path: string, params: Record<string, string> = {}): { url: URL; headers?: Record<string, string> } => {
    const url = new URL(`${TMDB_API}${path}`);
    for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
    if (isV4Token(key)) return { url, headers: { authorization: `Bearer ${key}` } };
    url.searchParams.set('api_key', key);
    return { url };
  };

  let id = moviedbId ?? null;
  if (id === null) {
    const find = authed(`/find/${imdbId}`, { external_source: 'imdb_id' });
    const found = await getJson<{ movie_results?: { id?: number }[]; tv_results?: { id?: number }[] }>(
      find.url,
      signal,
      find.headers
    );
    const first = kind === 'series' ? found?.tv_results?.[0]?.id : found?.movie_results?.[0]?.id;
    if (typeof first !== 'number') return [];
    id = first;
  }

  const path = kind === 'series' ? `/tv/${id}/aggregate_credits` : `/movie/${id}/credits`;
  const credits = authed(path);
  const data = await getJson<{ cast?: unknown }>(credits.url, signal, credits.headers);
  return kind === 'series' ? mapTmdbAggregateCast(data?.cast) : mapTmdbMovieCast(data?.cast);
}

/* ────────────────────────────────────────────────────────────────────────────
 * The cache, and the one public entry point
 * ──────────────────────────────────────────────────────────────────────────── */

const memory = new Map<string, CachedTitle>();
const request = sharedRequests<CastMember[]>();

/** Validate persisted data without deleting valid sibling rows or rewriting keys. */
function decodeCastCache(raw: unknown): Record<string, CachedTitle> {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return Object.fromEntries(Object.entries(raw).filter((entry): entry is [string, CachedTitle] => {
    const value: unknown = entry[1];
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
    const row = value as Record<string, unknown>;
    if (typeof row.at !== 'number' || !Number.isFinite(row.at) ||
        (row.tier !== 'tmdb' && row.tier !== 'keyless') ||
        (row.v !== undefined && (typeof row.v !== 'number' || !Number.isFinite(row.v))) ||
        !Array.isArray(row.members)) return false;
    return row.members.every((member: unknown) => {
      if (member === null || typeof member !== 'object' || Array.isArray(member)) return false;
      const fields = member as Record<string, unknown>;
      return typeof fields.name === 'string' &&
        ['character', 'photo', 'srcset', 'creditUrl', 'qid', 'born'].every(key =>
          fields[key] === undefined || typeof fields[key] === 'string');
    });
  }));
}

function readStored(key: string): CachedTitle | null {
  const file = readJson(KEYS.cast, {}, decodeCastCache);
  const hit = file[key];
  // A pre-`qid` (v1) row is a miss: its faces cannot link and its photos
  // predate the portrait preference. It stays on disk until the prune ages it
  // out or a fresh row overwrites it — either is fine.
  return hit && Array.isArray(hit.members) && hit.v === CACHE_VERSION ? hit : null;
}

function persist(key: string, entry: CachedTitle): void {
  const file = readJson(KEYS.cast, {}, decodeCastCache);
  file[key] = entry;
  writeJson(KEYS.cast, pruneCastCache(file, Date.now()));
}

/**
 * The cast of one title, cached per title in memory and localStorage so
 * revisiting does not refetch anybody's metadata. A row fetched keylessly is
 * refetched once a TMDB key appears (`tier`); the reverse never happens.
 * Returns [] when no source knows the title — the row simply does not render.
 */
export async function castFor(query: CastQuery, signal?: AbortSignal): Promise<CastMember[]> {
  signal?.throwIfAborted();
  const key = `${query.kind}:${query.imdbId}`;
  const wantTier: Tier = tmdbKey() ? 'tmdb' : 'keyless';

  const cached = memory.get(key) ?? readStored(key);
  if (cached && cached.tier === wantTier && Date.now() - cached.at < CACHE_TTL_MS) {
    memory.set(key, cached);
    return cached.members;
  }

  const swallow = (err: unknown): CastMember[] => {
    if ((err as Error)?.name === 'AbortError' || (err as Error)?.name === 'TimeoutError') throw err;
    return [];
  };
  const swallow2 = (err: unknown): Map<string, Portrait> => {
    if ((err as Error)?.name === 'AbortError' || (err as Error)?.name === 'TimeoutError') throw err;
    return new Map();
  };

  return request(`${key}:${wantTier}`, (parent) => catalogueDeadline(parent, CAST_DEADLINE_MS, 'Cast lookup', async (signal) => {
    let members: CastMember[] = [];
    let tier = wantTier;
    /** Wikidata rows get the TVMaze portrait upgrade; TMDB and TVMaze rows are
     *  already curated headshots and are left alone. */
    let wantPortraits = false;

    const apiKey = tmdbKey();
    if (apiKey) {
      members = await tmdbCast(query.kind, query.imdbId, query.moviedbId, apiKey, signal).catch(swallow);
      if (!members.length) tier = 'keyless';
    }
    if (!members.length) {
      members = await wikidataWorkCast(query.imdbId, query.names ?? [], signal).catch(swallow);
      wantPortraits = members.length > 0;
    }
    if (!members.length && query.kind === 'series') {
      members = await tvmazeCast(query.imdbId, signal).catch(swallow);
    }
    if (!members.length && query.names?.length) {
      members = await wikidataNameCast(query.names, signal).catch(swallow);
      wantPortraits = members.length > 0;
    }

    // The quality preference (header, docs/CAST.md § K): curated TVMaze
    // portraits over Commons candids. Fails soft to the members as they stand.
    if (wantPortraits) {
      const portraits =
        query.kind === 'series'
          ? await tvmazeShowPortraits(query.imdbId, signal).catch(swallow2)
          : await tvmazePeoplePortraits(members, signal).catch(swallow2);
      members = preferPortraits(members, portraits);
    }

    signal.throwIfAborted();
    if (members.length) {
      const entry: CachedTitle = { at: Date.now(), tier, members, v: CACHE_VERSION };
      memory.set(key, entry);
      persist(key, entry);
    }
    return members;
  }), signal);
}
